export const BOX_LIMIT = 20;
export const OHIO_SHIPPING = 1999;
const BOX_GRAMS = 9072;
const DISMISS_KEY = 'vnf-box-assistant-dismissed';

export function boxProgress(stats) {
  const grams=Math.max(0,Math.round(stats.capacityWeight*453.59237));
  const box=Math.max(1,Math.ceil(grams/BOX_GRAMS));
  const filled=grams-(box-1)*BOX_GRAMS;
  return {box,filled:filled/BOX_GRAMS*BOX_LIMIT,full:grams>0&&filled===BOX_GRAMS,completed:box-1,remaining:(BOX_GRAMS-filled)/BOX_GRAMS*BOX_LIMIT};
}

export function cartBoxStats(cart, products) {
  const byId=new Map(products.map(item=>[Number(item.id),item]));
  let weight=0,capacityWeight=0,blocked=false,unknown=false;
  const held={};
  for(const line of cart.items||[]) {
    const components=line.item_components?.length?line.item_components:[line];
    for(const component of components) {
      const id=Number(component.variant_id||component.id),quantity=Number(component.quantity)||0;
      held[id]=(held[id]||0)+quantity;
      const item=byId.get(id);
      if(item?.pickupOnly)blocked=true;
      if(component.requires_shipping===false&&!item?.meat)continue;
      const match=String(component.variant_title||component.title||'').match(/(?:^|[^/\d.])(\d*\.?\d+)\s*lbs?\b/i);
      const packWeight=item?.weight||Number(match?.[1])||Number(component.grams)/453.59237;
      if(!packWeight){unknown=true;continue;}
      weight+=packWeight*quantity;
      capacityWeight+=Math.max(packWeight,item?.shipWeight||Number(component.grams)/453.59237||0)*quantity;
    }
  }
  return {weight,capacityWeight,held,blocked,unknown,total:Number(cart.total_price)||0};
}

export function buildBoxFill(products, stats) {
  const remaining=BOX_LIMIT-stats.capacityWeight;
  if(stats.blocked||stats.unknown||remaining<0.1)return null;
  const limit=Math.floor((remaining+1e-8)*100);
  const groups=new Map();
  for(const item of products) {
    const stock=Math.max(0,item.stock-(stats.held[item.id]||0));
    if(item.pickupOnly||!item.meat||item.kind==='offal'||stock<1||item.price<=0||item.weight<=0)continue;
    const units=Math.ceil(Math.max(item.weight,item.shipWeight||0)*100-1e-8);
    if(units>limit)continue;
    if(!groups.has(item.handle))groups.set(item.handle,[]);
    groups.get(item.handle).push({...item,stock,units});
  }
  let states=new Map([[0,{units:0,price:0,steaks:0,count:0,previous:null,item:null,quantity:0}]]);
  for(const variants of groups.values()) {
    const next=new Map(states);
    for(const state of states.values())for(const item of variants) {
      const maximum=Math.min(Math.floor(item.stock),item.kind==='ground'?5:item.kind==='patties'?3:1);
      for(let quantity=1;quantity<=maximum;quantity++) {
        const units=state.units+item.units*quantity;
        if(units>limit)break;
        const steaks=state.steaks+(item.kind==='steak'?quantity:0);
        if(steaks>1)break;
        const candidate={units,price:state.price+item.price*quantity,steaks,count:state.count+quantity,previous:state,item,quantity};
        const existing=next.get(units);
        if(!existing || steaks<existing.steaks || (steaks===existing.steaks && candidate.price<existing.price))next.set(units,candidate);
      }
    }
    states=next;
  }
  const best=[...states.values()].sort((a,b)=>b.units-a.units||a.steaks-b.steaks||a.price-b.price)[0];
  if(!best?.units)return null;
  const lines=[];for(let state=best;state;state=state.previous)if(state.item)lines.push({...state.item,quantity:state.quantity});lines.reverse();
  const addedWeight=lines.reduce((sum,item)=>sum+item.weight*item.quantity,0);
  return {lines,addedWeight,weight:stats.weight+addedWeight,subtotal:stats.total+best.price,addedPrice:best.price,key:lines.map(item=>item.id+':'+item.quantity).sort().join('|')};
}

if(typeof customElements!=='undefined'&&!customElements.get('vnf-fill-box'))customElements.define('vnf-fill-box',class extends HTMLElement {
  connectedCallback() {
    if(this.listeners)return;
    this.listeners=new AbortController();
    const options={signal:this.listeners.signal};
    this.products=JSON.parse(this.querySelector('[data-fill-stock]').textContent);
    this.messages=JSON.parse(this.querySelector('[data-fill-messages]').textContent);
    this.dialog=this.querySelector('dialog');
    this.dismissed=this.sessionValue(DISMISS_KEY)==='1';
    this.querySelector('[data-fill-close]').addEventListener('click',()=>this.dismiss(),options);
    this.querySelector('[data-fill-checkout]').addEventListener('click',()=>this.continueCheckout(),options);
    this.querySelector('[data-fill-add]').addEventListener('click',()=>this.add(),options);
    this.dialog.addEventListener('cancel',event=>{event.preventDefault();this.dismiss();},options);
    this.dialog.addEventListener('click',event=>{if(event.target===this.dialog)this.dismiss();},options);
    document.addEventListener('click',event=>{
      if(!(event.target instanceof Element))return;
      if(event.target.closest('[data-box-close]'))this.dismiss();
      else if(event.target.closest('[data-fill-box-open]'))this.open();
    },options);
    document.addEventListener('cart:update',event=>this.onCart(event),options);
    document.addEventListener('submit',event=>{
      if(event.target instanceof HTMLFormElement&&event.submitter?.name==='checkout')this.onCheckout(event);
    },{...options,capture:true});
    document.addEventListener('dialog:open',()=>this.scheduleTrackers(),{...options,capture:true});
    document.addEventListener('dialog:close',()=>this.scheduleTrackers(),{...options,capture:true});
    window.addEventListener('pageshow',event=>{if(event.persisted)this.syncCart();},options);
    this.observer=new MutationObserver(records=>{
      if(records.some(record=>[...record.addedNodes].some(node=>node instanceof Element&&(node.matches('[data-box-tracker]')||node.querySelector('[data-box-tracker]')))))this.scheduleTrackers();
    });
    this.observer.observe(document.body,{childList:true,subtree:true});
    this.updateCart(JSON.parse(this.querySelector('[data-fill-cart]').textContent));
  }
  disconnectedCallback() {
    this.listeners?.abort();this.listeners=null;this.request?.abort();this.observer?.disconnect();
    clearTimeout(this.syncTimer);cancelAnimationFrame(this.trackerFrame);
  }
  sessionValue(key,value) {
    try {if(value===undefined)return sessionStorage.getItem(key);sessionStorage.setItem(key,value);}catch{}
  }
  dismiss() {
    if(this.busy)return;
    this.dismissed=true;this.sessionValue(DISMISS_KEY,'1');
    this.checkoutIntent=null;
    this.request?.abort();
    if(this.dialog.open)this.dialog.close();
    this.renderTrackers();
  }
  async onCheckout(event) {
    if(this.resumingCheckout)return;
    if(this.checkoutIntent){event.preventDefault();event.stopImmediatePropagation();return;}
    event.preventDefault();event.stopImmediatePropagation();
    const intent={form:event.target,submitter:event.submitter};
    this.checkoutIntent=intent;
    if(!await this.open(true))this.resumeCheckout(intent);
  }
  continueCheckout() {
    if(this.busy)return;
    if(this.checkoutIntent){this.resumeCheckout(this.checkoutIntent);return;}
    if(this.dialog.open)this.dialog.close();
    this.renderTrackers();
    location.assign((window.Shopify?.routes?.root||'/')+'checkout');
  }
  resumeCheckout(intent) {
    if(!intent)return;
    this.checkoutIntent=null;
    if(this.dialog.open)this.dialog.close();
    this.renderTrackers();
    const form=intent.form.isConnected?intent.form:document.getElementById(intent.form.id);
    if(!(form instanceof HTMLFormElement)){
      location.assign((window.Shopify?.routes?.root||'/')+'checkout');
      return;
    }
    let submitter=intent.submitter;
    if(!submitter?.isConnected||submitter.form!==form)submitter=[...document.querySelectorAll('[name="checkout"]')].find(button=>button.form===form&&!button.disabled);
    let temporary;
    if(!submitter){
      temporary=document.createElement('button');temporary.type='submit';temporary.name='checkout';temporary.hidden=true;form.append(temporary);submitter=temporary;
    }
    this.resumingCheckout=true;
    try {HTMLFormElement.prototype.requestSubmit.call(form,submitter);}
    finally {this.resumingCheckout=false;temporary?.remove();}
  }
  money(cents) {return new Intl.NumberFormat(document.documentElement.lang||'en-US',{style:'currency',currency:this.dataset.currency}).format(cents/100);}
  pounds(weight) {return (Math.round(weight*100)/100).toFixed(2).replace(/0$/,'').replace(/\.0$/,'');}
  message(key,values={}) {
    let result=this.messages[key]||'';
    for(const [name,value] of Object.entries(values))result=result.replaceAll('__'+name.toUpperCase()+'__',String(value));
    return result;
  }
  updateCart(cart) {
    this.cart=cart;this.stats=cartBoxStats(cart,this.products);this.renderTrackers();
  }
  scheduleTrackers() {
    cancelAnimationFrame(this.trackerFrame);this.trackerFrame=requestAnimationFrame(()=>this.renderTrackers());
  }
  renderTrackers() {
    if(!this.stats)return;
    const state=boxProgress(this.stats),valid=!this.stats.blocked&&!this.stats.unknown;
    const status=this.stats.blocked?this.message('pickup_order'):this.stats.unknown?this.message('unknown'):state.full?this.message('box_full',{box:state.box}):this.message('box_status',{box:state.box,weight:this.pounds(state.filled)});
    const room=state.full?this.message('next_box',{next:state.box+1}):this.message('room',{room:this.pounds(state.remaining)});
    const drawerOpen=!!document.querySelector('cart-drawer-component dialog[open]');
    for(const tracker of document.querySelectorAll('[data-box-tracker]')) {
      const floating=tracker.hasAttribute('data-box-floating');
      tracker.hidden=this.dismissed||!(this.cart.items?.length)||this.stats.capacityWeight<=0||(floating&&(drawerOpen||this.dialog.open||this.dataset.cartPage==='true'));
      tracker.querySelector('[data-box-status]').textContent=status;
      const progress=tracker.querySelector('progress');progress.value=valid?state.filled:0;progress.hidden=!valid;
      progress.setAttribute('aria-valuetext',status);
      const hint=tracker.querySelector('[data-box-room]');hint.textContent=valid?room:'';hint.hidden=!valid;
      const completed=tracker.querySelector('[data-box-completed]');
      completed.hidden=state.completed<1||!valid;completed.textContent=state.completed>0?this.message('box_full',{box:state.completed}):'';
      const open=tracker.querySelector('[data-fill-box-open]');open.hidden=!valid||this.stats.capacityWeight>=BOX_LIMIT-0.1;
    }
    const progress=this.querySelector('[data-fill-progress]');progress.value=valid?state.filled:0;progress.hidden=!valid;progress.setAttribute('aria-valuetext',status);
    this.querySelector('[data-fill-status]').textContent=status;
    this.querySelector('[data-fill-room]').textContent=valid?(state.full?room:this.message('popup_room',{room:this.pounds(state.remaining)})):'';
  }
  onCart(event) {
    this.request?.abort();
    const resource=event.detail?.resource;
    if(Array.isArray(resource?.items)){this.updateCart(resource);this.scheduleTrackers();}
    else {clearTimeout(this.syncTimer);this.syncTimer=setTimeout(()=>this.syncCart(),80);}
    if(this.dialog.open){this.refresh().then(()=>this.render()).catch(()=>{});return;}
  }
  async syncCart() {
    try {await this.refresh(false);}catch{}
  }
  async refresh(stock=true) {
    this.request?.abort();const request=new AbortController();this.request=request;
    const options={cache:'no-store',signal:request.signal};
    const url=new URL(window.Shopify?.routes?.root||'/',location.origin);
    url.searchParams.set('section_id',this.dataset.sectionId);url.searchParams.set('_vnf_stock',Date.now());
    const [cartResponse,stockResponse]=await Promise.all([fetch(this.dataset.cartUrl+'.js',options),stock?fetch(url,options):Promise.resolve(null)]);
    if(!cartResponse.ok||(stockResponse&&!stockResponse.ok))throw new Error(this.messages.error);
    const cart=await cartResponse.json();
    if(stockResponse){
      const page=new DOMParser().parseFromString(await stockResponse.text(),'text/html');
      const data=page.querySelector('[data-fill-stock]');if(!data)throw new Error(this.messages.error);
      if(request.signal.aborted)throw new DOMException('Aborted','AbortError');
      this.products=JSON.parse(data.textContent);
    }
    if(request.signal.aborted)throw new DOMException('Aborted','AbortError');
    this.updateCart(cart);
    if(stock)this.suggestion=buildBoxFill(this.products,this.stats);
  }
  render() {
    this.renderTrackers();
    const list=this.querySelector('[data-fill-lines]');list.replaceChildren();
    const suggestion=this.suggestion;
    this.querySelector('[data-fill-choice]').hidden=!suggestion;
    const empty=this.querySelector('[data-fill-empty]');empty.hidden=!!suggestion;
    if(!suggestion){
      const state=boxProgress(this.stats||{capacityWeight:0});
      empty.textContent=this.stats?.blocked?this.message('pickup_order'):this.stats?.unknown?this.message('unknown'):state.full?this.message('next_box',{next:state.box+1}):this.messages.empty;
      return;
    }
    this.querySelector('[data-fill-intro]').textContent=this.message('intro',{full:this.pounds(suggestion.weight)});
    for(const item of suggestion.lines) {
      const row=document.createElement('li'),name=document.createElement('span'),price=document.createElement('strong');
      name.textContent=item.quantity+' × '+item.title;
      price.textContent=this.money(item.price*item.quantity);row.append(name,price);list.append(row);
    }
    const delivered=suggestion.subtotal+OHIO_SHIPPING;
    this.querySelector('[data-fill-pickup-perlb]').textContent=this.message('perlb_value',{price:this.money(Math.round(suggestion.subtotal/suggestion.weight))});
    this.querySelector('[data-fill-pickup-total]').textContent=this.message('total_value',{price:this.money(suggestion.subtotal)});
    this.querySelector('[data-fill-total]').textContent=this.message('total_value',{price:this.money(delivered)});
    this.querySelector('[data-fill-perlb]').textContent=this.message('perlb_value',{price:this.money(Math.round(delivered/suggestion.weight))});
    this.querySelector('[data-fill-add]').textContent=this.message(this.checkoutIntent?'add_checkout':'add',{price:this.money(suggestion.addedPrice)});
  }
  async open(automatic=false) {
    if(this.loading||this.busy||this.dialog.open)return false;
    if(!automatic){this.dismissed=false;this.sessionValue(DISMISS_KEY,'0');}
    this.loading=true;
    const timeout=automatic?setTimeout(()=>this.request?.abort(),4000):null;
    try {
      await this.refresh();
      if(automatic&&(!this.cart.items?.length||!this.suggestion))return false;
      if(this.checkoutIntent)await document.querySelector('cart-drawer-component')?.closeDialog?.();
      this.querySelector('[data-fill-error]').hidden=true;this.render();this.dialog.showModal();this.renderTrackers();
      return true;
    }catch(failure){
      if(failure.name!=='AbortError'&&!automatic){
        this.suggestion=null;this.render();const error=this.querySelector('[data-fill-error]');error.textContent=failure.message||this.messages.error;error.hidden=false;this.dialog.showModal();this.renderTrackers();
      }
      return false;
    }finally{clearTimeout(timeout);this.loading=false;}
  }
  async add() {
    if(this.busy||!this.suggestion)return;
    this.busy=true;
    const button=this.querySelector('[data-fill-add]'),error=this.querySelector('[data-fill-error]'),previous=this.suggestion;
    button.disabled=true;button.textContent=this.messages.busy;error.hidden=true;
    try {
      await this.refresh();this.render();button.textContent=this.messages.busy;
      if(this.suggestion?.key!==previous.key||this.suggestion?.subtotal!==previous.subtotal)throw new Error(this.messages.changed);
      const items=this.suggestion.lines.map(item=>({id:item.id,quantity:item.quantity,properties:{'Box fill':'20 lb meat box'}}));
      const response=await fetch(this.dataset.addUrl+'.js',{method:'POST',headers:{'Content-Type':'application/json',Accept:'application/json'},body:JSON.stringify({items})});
      if(!response.ok){const result=await response.json();await this.refresh();this.render();throw new Error(result.description||this.messages.error);}
      if(this.checkoutIntent){this.resumeCheckout(this.checkoutIntent);return;}
      await this.refresh(false);location.assign(this.dataset.cartUrl);
    }catch(failure){if(failure.name!=='AbortError'){error.textContent=failure.message||this.messages.error;error.hidden=false;}}
    finally{this.busy=false;button.disabled=false;if(this.suggestion)button.textContent=this.message(this.checkoutIntent?'add_checkout':'add',{price:this.money(this.suggestion.addedPrice)});}
  }
});

